// TypeScript types for Data Cloud module
import React from 'react';

// =============================================================================
// WORKFLOW CONFIGURATION
// =============================================================================

export interface DataCloudWorkflowStep {
  id: number;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

export const WORKFLOW_STEPS: DataCloudWorkflowStep[] = [
  { id: 1, label: 'workflow.steps.upload.label', description: 'workflow.steps.upload.description' },
  { id: 2, label: 'workflow.steps.parameters.label', description: 'workflow.steps.parameters.description' },
  { id: 3, label: 'workflow.steps.processing.label', description: 'workflow.steps.processing.description' },
  { id: 4, label: 'workflow.steps.interpolation.label', description: 'workflow.steps.interpolation.description' },
  { id: 5, label: 'workflow.steps.results.label', description: 'workflow.steps.results.description' }
];

export const INITIAL_STEP = 1;

// =============================================================================
// DATA TYPES
// =============================================================================

export interface DataPoint {
  x: number;
  y: number;
  predicted?: number;
  upperBound?: number;
  lowerBound?: number;
  regressionValue?: number | null;
}

export interface InterpolatedPoint {
  UTC: string;
  value: number;
}

export interface InterpolationInfo {
  addedPoints: number;
  totalPoints: number;
  removedPoints: number;
}

export interface RegressionResponse {
  success: boolean;
  data?: {
    x_values: number[];
    y_values: number[];
    predicted_y?: number[];
    upper_bound?: number[];
    lower_bound?: number[];
    removed_points?: number;
    headers?: {
      temp_file?: string[];
      load_file?: string[];
    };
  };
}

// =============================================================================
// UI STATE MANAGEMENT
// =============================================================================

/**
 * Structured error payload for SET_ERROR dispatch. The reducer stores:
 *   - code: BE error_code or undefined for FE-side errors
 *   - suggestion: BE suggestions[0] (localized) or undefined
 *   - fallback: auto-English text used when neither i18n key nor suggestion is available
 *
 * SET_ERROR also accepts `null` to clear the error state (mutex with success).
 */
export interface CloudErrorPayload {
  code: string | undefined;
  suggestion: string | undefined;
  fallback: string;
  /** W10-FE1: when true, ErrorMessage does NOT auto-dismiss (duration=0). */
  persistent?: boolean;
}

/**
 * Structured success payload for SET_SUCCESS dispatch. The reducer stores:
 *   - key: i18n key in the `dataCloud` namespace (e.g. 'success.dataProcessedWithRemoval')
 *   - params: optional interpolation params (e.g. { count: 5 })
 *
 * The page translates at render time via `t(key, params)` so language switches
 * during a visible toast re-render the message in the new language.
 *
 * SET_SUCCESS also accepts `null` to clear the success state.
 */
export interface CloudSuccessPayload {
  key: string;
  params?: Record<string, unknown>;
}

export interface DataCloudUIState {
  currentStep: number;
  completedSteps: number[];
  isNameModalOpen: boolean;
  saveDataName: string;
  errorMessage: string | null;
  /** BE error_code (e.g. 'TIMESTAMP_MISMATCH'). Drives FE i18n via cloudBackendErrorMapper. */
  errorCode: string | null;
  /** BE-supplied localized suggestion (suggestions[0]). Render-time fallback when no i18n key matches. */
  errorSuggestion: string | null;
  /** W10-FE1: derived from CloudErrorPayload.persistent. When true, ErrorMessage stays until user dismisses. */
  errorPersistent: boolean;
  /** i18n key in `dataCloud` namespace for the active success toast. */
  successKey: string | null;
  /** Interpolation params for `successKey`, used by `t(key, params)` at render time. */
  successParams: Record<string, unknown> | null;
  isLoading: boolean;
  isInterpolating: boolean;
  showFileUpload: boolean;
}

export type DataCloudUIAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'MARK_STEP_COMPLETED'; payload: number }
  | { type: 'TOGGLE_MODAL'; payload: boolean }
  | { type: 'SET_SAVE_NAME'; payload: string }
  | { type: 'SET_ERROR'; payload: CloudErrorPayload | null }
  | { type: 'SET_SUCCESS'; payload: CloudSuccessPayload | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_INTERPOLATING'; payload: boolean }
  | { type: 'RESET_UI' };

// =============================================================================
// PARAMETER TYPES
// =============================================================================

export type ToleranceType = 'cnt' | 'dep';
export type RegressionType = 'lin' | 'poly';

export interface RegressionParameters {
  toleranceType: ToleranceType;
  regressionType: RegressionType;
  toleranceCnt: string;
  toleranceDep: string;
  toleranceDepExtra: string;
}

export interface ChartDisplayParameters {
  pointSize: string;
  showAllDataPoints: boolean;
}

// =============================================================================
// VALIDATION RULES
// =============================================================================

export const VALIDATION_RULES = {
  toleranceCnt: { min: 1, max: 1000, pattern: /^\d+$/ },
  toleranceDep: { min: 0, max: 100, pattern: /^\d*\.?\d*$/ },
  toleranceDepExtra: { min: 0, max: 100, pattern: /^\d*\.?\d*$/ },
  maxTimeSpan: { min: 1, max: 525600, pattern: /^\d+$/ }, // 1 year in minutes
  pointSize: { min: 1, max: 50, pattern: /^\d+$/ }
};

export interface ValidationError {
  field: string;
  message: string;
}

export const validateNumericField = (
  value: string,
  field: keyof typeof VALIDATION_RULES
): string | null => {
  const rules = VALIDATION_RULES[field];
  if (!rules) return null;

  if (value === '') return null; // Empty is allowed (for clearing)

  if (rules.pattern && !rules.pattern.test(value)) {
    return `Ungültiges Format`;
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return `Muss eine Zahl sein`;
  }

  if (numValue < rules.min) {
    return `Minimum: ${rules.min}`;
  }

  if (numValue > rules.max) {
    return `Maximum: ${rules.max}`;
  }

  return null;
};

// =============================================================================
// DROPDOWN OPTIONS
// =============================================================================

export const REGRESSION_TYPE_OPTIONS = [
  { value: 'lin', label: 'Linear (lin)' },
  { value: 'poly', label: 'Polynomial (poly)' }
] as const;

export const TOLERANCE_TYPE_OPTIONS = [
  { value: 'cnt', label: 'Count-Based (CNT)' },
  { value: 'dep', label: 'Dependent (DEP)' }
] as const;

// =============================================================================
// DEFAULT VALUES
// =============================================================================

export const DEFAULT_PARAMETERS: RegressionParameters = {
  toleranceType: 'cnt',
  regressionType: 'lin',
  toleranceCnt: '100',
  toleranceDep: '0.0',
  toleranceDepExtra: '0.0'
};

export const DEFAULT_CHART_DISPLAY: ChartDisplayParameters = {
  pointSize: '12',
  showAllDataPoints: true // Always show all data points (sampling removed)
};

// =============================================================================
// CONSTANTS
// =============================================================================

export const MAX_CHART_POINTS = 10000;
export const DEFAULT_X_LABEL = 'X';
export const DEFAULT_Y_LABEL = 'Y';
